/**
 * ClaimFormDialog — New / Edit for Sales Control › Claim.
 * Converted 1:1 from Access `frmCtrlSalesQryClaimAdd` (UNBOUND form — values
 * pushed/read via VBA). 2-col layout (Access input Left 2676 = col1, 9684 = col2),
 * row order theo Top. Combos = Autocomplete flat "<CODE> — <NAME>".
 *
 * Cascade (claim_type_AfterUpdate): Weight claims → quality+other disabled;
 *   Quality claims → quality ENABLED / other disabled; Others → other ENABLED /
 *   quality disabled; else both disabled. Always clear quality+other on change.
 *
 * CheckStatus (recompute on claim_date / claim_type / conditions / discharge_date /
 *   weighing_date change): DLookup public_claim_condition offsets via
 *   useLazyGetClaimConditionSnapshotQuery(organization = conditions) →
 *   {weighingDate, weightClaim, qualityClaims, others} day offsets.
 *     valid_claim_date = discharge_date + (type offset)  [empty if no discharge_date]
 *     valid_weighing_date = weighing_date + weighingDate offset  (Weight claims only)
 *     accept_or_reject: Weight → Accept iff weighing_date < valid_weighing_date AND
 *       claim_date <= valid_claim_date ; other types → Accept iff claim_date <=
 *       valid_claim_date ; else Reject.
 *   These 3 fields are display-only (NOT sent to BE).
 *
 * Note claim_type: Access stores claim_type.column(1) (= type_claim label, one of
 * "Weight claims"/"Quality claims"/"Others") and every rule compares that label,
 * so we key the combo by label (display "<code> — <label>").
 */
import { useEffect, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type ClaimLookupItem,
  type SaleClaimWrite,
  useLazySuggestClaimNoQuery,
  useLazyCheckClaimNoQuery,
  useLazyGetSaleClaimFormQuery,
  useLazyGetClaimConditionSnapshotQuery,
  useClaimLookupCustomerInvoicesQuery,
  useClaimLookupTypesQuery,
  useClaimLookupConditionsQuery,
  useClaimLookupPriceUnitsQuery,
  useCreateSaleClaimMutation,
  useUpdateSaleClaimMutation,
} from '../../store/api/saleControlApi'
import { FORM_GRID_2COL, TEXT_TRUNCATE } from '../../theme/responsive'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface Props {
  open: boolean
  mode: 'new' | 'edit'
  claimId?: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  /** Access claim_no_index — user gõ số, code = "CLAIM-<index>" (claim_no_index_AfterUpdate). */
  claimNoIndex: string
  claimScsCode: string
  customerInvoiceNumber: string
  claimType: string // stores the type_claim LABEL (Access column(1))
  conditions: string
  finalStatus: string
  priceUnit: string
  claimDate: string
  dischargeDate: string
  weighingDate: string
  claimReason: string
  claimQualityValue: string
  claimOtherValue: string
  roe: string
  note: string
  // display-only computed (NOT sent to BE)
  validWeighingDate: string
  validClaimDate: string
  acceptOrReject: string
}

const EMPTY: Draft = {
  claimNoIndex: '', claimScsCode: '', customerInvoiceNumber: '', claimType: '', conditions: '',
  finalStatus: '', priceUnit: '', claimDate: '', dischargeDate: '', weighingDate: '',
  claimReason: '', claimQualityValue: '', claimOtherValue: '', roe: '', note: '',
  validWeighingDate: '', validClaimDate: '', acceptOrReject: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

const FINAL_STATUS_OPTS: ClaimLookupItem[] = [
  { value: 'Reject', label: 'Reject' },
  { value: 'Accept', label: 'Accept' },
]

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
/** Access Format="Standard": 2 decimals + thousand separator, right-aligned. */
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))
const off = (n: number | null | undefined): number => Number(n ?? 0) || 0

/** Inject current value into options so chip shows before list load / when filtered out. */
function withCurrent(options: ClaimLookupItem[], value: string, label?: string): ClaimLookupItem | null {
  if (!value) return null
  return options.find((o) => o.value === value) ?? { value, label: label || value }
}

/* ───────── Reusable combo (value-keyed) ───────── */
function Combo(props: {
  label: string
  value: string
  valueLabel?: string
  options: ClaimLookupItem[]
  loading?: boolean
  disabled?: boolean
  labelOnly?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string, item: ClaimLookupItem | null) => void
}) {
  const { label, value, valueLabel, options, loading, disabled, labelOnly, onInputChange, onPick } = props
  const sel = withCurrent(options, value, valueLabel)
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  const getLabel = (o: ClaimLookupItem) =>
    !o ? '' : labelOnly || o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`
  return (
    <Autocomplete
      size={SM}
      fullWidth
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '', v)}
      onInputChange={onInputChange ? (_e, v, reason) => { if (reason === 'input') onInputChange(v) } : undefined}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  )
}

export default function ClaimFormDialog({ open, mode, claimId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  // Edit hydrate hits a view query → show a loading state until the API response
  // lands, then render the fields (avoid a flash of empty inputs).
  const [formLoading, setFormLoading] = useState(false)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [suggestClaimNo] = useLazySuggestClaimNoQuery()
  const [checkClaimNo] = useLazyCheckClaimNoQuery()
  const [loadForm] = useLazyGetSaleClaimFormQuery()
  const [fetchSnapshot] = useLazyGetClaimConditionSnapshotQuery()
  const [createClaim, { isLoading: creating }] = useCreateSaleClaimMutation()
  const [updateClaim, { isLoading: updating }] = useUpdateSaleClaimMutation()
  const saving = creating || updating

  // Lookups — fresh on open
  const skip = { skip: !open }
  const [invQ, setInvQ] = useState('')
  const customerInvoices = useClaimLookupCustomerInvoicesQuery(invQ || undefined, skip)
  const types = useClaimLookupTypesQuery(undefined, skip)
  const conditions = useClaimLookupConditionsQuery(undefined, skip)
  const priceUnits = useClaimLookupPriceUnitsQuery(undefined, skip)

  // ── Hydrate on open ──
  useEffect(() => {
    if (!open) return
    let cancelled = false
    if (mode !== 'new') setFormLoading(true) // show loading immediately for Edit
    void (async () => {
      if (mode === 'new') {
        setFormLoading(false)
        const resp = await suggestClaimNo().unwrap().catch(() => null)
        if (cancelled) return
        // New → auto-fill Claim date = hôm nay (local, yyyy-MM-dd cho input date).
        const now = new Date()
        const today = `${now.getFullYear()}-${`${now.getMonth() + 1}`.padStart(2, '0')}-${`${now.getDate()}`.padStart(2, '0')}`
        setDraft({
          ...EMPTY,
          claimNoIndex: resp?.index != null ? String(resp.index) : '',
          claimScsCode: resp?.claimScsCode ?? '',
          claimDate: today,
        })
        return
      }
      if (!claimId) { setFormLoading(false); return }
      const f = await loadForm(claimId).unwrap().catch(() => null)
      if (cancelled) return
      if (!f) { setFormLoading(false); return }
      const g = (k: string) => s((f as Record<string, unknown>)[k])
      setDraft({
        ...EMPTY,
        // Claim No. (index) = phần sau prefix "CLAIM-" (Edit khoá, chỉ hiển thị).
        claimNoIndex: g('claimScsCode').replace(/^CLAIM-/, ''),
        claimScsCode: g('claimScsCode'),
        customerInvoiceNumber: g('customerInvoiceNo'),
        claimType: g('claimType'),
        conditions: g('claimCondition'),
        finalStatus: g('finalStatus'),
        priceUnit: g('priceUnit'),
        claimDate: g('dateClaim').slice(0, 10),
        dischargeDate: g('dischargeDate').slice(0, 10),
        weighingDate: g('weighingDate').slice(0, 10),
        claimReason: g('claimReason'),
        claimQualityValue: fmtStd(g('claimQualityValue')),
        claimOtherValue: fmtStd(g('claimOtherValue')),
        roe: fmtStd(g('roe')),
        note: g('note'),
      })
      setFormLoading(false)
    })()
    return () => { cancelled = true }
  }, [open, mode, claimId, suggestClaimNo, loadForm])

  // ── CheckStatus: recompute valid dates + accept/reject (display-only) ──
  useEffect(() => {
    if (!open) return
    let cancelled = false
    const compute = (snap: { weighingDate: number | null; weightClaim: number | null; qualityClaims: number | null; others: number | null } | null) => {
      if (cancelled) return
      const type = draft.claimType
      const disc = draft.dischargeDate
      const weigh = draft.weighingDate
      const cd = draft.claimDate
      let vcd = ''
      let vwd = ''
      if (type === 'Quality claims') {
        if (disc) vcd = dayjs(disc).add(off(snap?.qualityClaims), 'day').format('YYYY-MM-DD')
      } else if (type === 'Others') {
        if (disc) vcd = dayjs(disc).add(off(snap?.others), 'day').format('YYYY-MM-DD')
      } else if (type === 'Weight claims') {
        if (disc) vcd = dayjs(disc).add(off(snap?.weightClaim), 'day').format('YYYY-MM-DD')
        if (disc && weigh) vwd = dayjs(weigh).add(off(snap?.weighingDate), 'day').format('YYYY-MM-DD')
      }
      let ar = ''
      if (type === 'Weight claims') {
        ar = weigh && vwd && cd && vcd && dayjs(weigh).isBefore(dayjs(vwd)) && !dayjs(cd).isAfter(dayjs(vcd))
          ? 'Accept' : 'Reject'
      } else if (type) {
        ar = cd && vcd && !dayjs(cd).isAfter(dayjs(vcd)) ? 'Accept' : 'Reject'
      }
      setDraft((p) => ({ ...p, validClaimDate: vcd, validWeighingDate: vwd, acceptOrReject: ar }))
    }
    if (!draft.conditions) { compute(null); return }
    void fetchSnapshot(draft.conditions).unwrap()
      .then((snap) => compute(snap))
      .catch(() => compute(null))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft.claimType, draft.conditions, draft.claimDate, draft.dischargeDate, draft.weighingDate])

  // Cascade: enable state derived from claim_type (applies on Edit open too).
  const qualityEnabled = draft.claimType === 'Quality claims'
  const otherEnabled = draft.claimType === 'Others'

  const handleTypePick = (label: string) => {
    // claim_type_AfterUpdate: always clear both quality/other values on change.
    setDraft((p) => ({ ...p, claimType: label, claimQualityValue: '', claimOtherValue: '' }))
  }

  // claim_no_index_AfterUpdate: code = "CLAIM-" + index; check trùng khi blur →
  // warn + CLEAR code (giữ index cho user sửa) — đúng hành vi Access.
  const handleIndexChange = (v: string) => {
    const idx = v.replace(/[^\d]/g, '')
    setDraft((p) => ({ ...p, claimNoIndex: idx, claimScsCode: idx ? `CLAIM-${idx}` : '' }))
  }
  const handleIndexBlur = async () => {
    const code = draft.claimNoIndex.trim() ? `CLAIM-${draft.claimNoIndex.trim()}` : ''
    if (!code || mode === 'edit') return
    const resp = await checkClaimNo({ claimScsCode: code }).unwrap().catch(() => null)
    if (resp?.exists) {
      snack.warning('Claim No is existed!')
      setDraft((p) => ({ ...p, claimScsCode: '' }))
    }
  }

  const indexEmpty = !draft.claimNoIndex.trim()
  const claimCodeEmpty = !draft.claimScsCode.trim()
  const invoiceEmpty = !draft.customerInvoiceNumber.trim()
  const claimDateEmpty = !draft.claimDate.trim()
  const canSave = canEdit && !indexEmpty && !claimCodeEmpty && !invoiceEmpty && !claimDateEmpty && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    // Access btnSave errCheck order 2→5 (verbatim English messages).
    if (indexEmpty) { snack.error('Please type Claim Index!'); return }
    if (invoiceEmpty) { snack.error('Please choose Invoice number!'); return }
    if (claimCodeEmpty) { snack.error('Please choose to generate Claim Code!'); return }
    if (claimDateEmpty) { snack.error('Please choose Claim Date!'); return }
    if (!canSave) return
    const payload: SaleClaimWrite = {
      claimScsCode: draft.claimScsCode,
      customerInvoiceNo: draft.customerInvoiceNumber,
      dateClaim: draft.claimDate || null,
      claimType: draft.claimType || null,
      claimCondition: draft.conditions || null,
      dischargeDate: draft.dischargeDate || null,
      weighingDate: draft.weighingDate || null,
      finalStatus: draft.finalStatus || null,
      priceUnit: draft.priceUnit || null,
      claimReason: draft.claimReason || null,
      claimQualityValue: parseNum(draft.claimQualityValue),
      claimOtherValue: parseNum(draft.claimOtherValue),
      roe: parseNum(draft.roe),
      note: draft.note || null,
    }
    try {
      if (mode === 'edit' && claimId) {
        await updateClaim({ id: claimId, body: payload }).unwrap()
        snack.success(`Claim ${draft.claimScsCode} updated`)
      } else {
        await createClaim(payload).unwrap()
        snack.success(`Claim ${draft.claimScsCode} added`)
      }
      onSaved()
      onClose()
    } catch (err) {
      // Duplicate on New surfaces BE 422 "This Claim No is existed!".
      snack.error(getErrorMessage(err, 'Save claim failed'))
    }
  }

  const title = mode === 'new' ? 'New' : 'Edit'

  const dateField = (key: keyof Draft, label: string, required?: boolean) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, required, InputLabelProps: { shrink: true } } }}
    />
  )
  const numField = (key: keyof Draft, label: string, disabled?: boolean) => (
    <TextField
      label={label} size={SM} fullWidth type="text" disabled={disabled} value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      onBlur={() => set(key, fmtStd(draft[key] as string) as Draft[typeof key])}
      inputProps={numProps}
    />
  )
  const textField = (key: keyof Draft, label: string, extra?: object) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])} {...extra} />
  )
  const roField = (key: keyof Draft, label: string) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
      inputProps={{ tabIndex: -1 }} sx={readonlyFieldSx} />
  )

  // claim_type — keyed by LABEL (Access stores column(1) = type_claim label).
  const typeOpts = types.data?.items ?? []
  const typeSel = typeOpts.find((o) => o.label === draft.claimType)
    ?? (draft.claimType ? { value: draft.claimType, label: draft.claimType } : null)
  const typeMerged = typeSel && !typeOpts.some((o) => o.label === typeSel.label) ? [typeSel, ...typeOpts] : typeOpts

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" noWrap title={`${title} — Claim`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700 }}>{title} — Claim</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {formLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
        <>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit claims.</Alert>}
        {/* 2-col grid 1:1 Access (col1 Left=2676 / col2 Left=9684), rows theo Top:
            R1 claim_scs_id | — · R2 claim_no_index | customer_invoice ·
            R3 claim_scs_code | claim_date · R4 claim_type | conditions ·
            R5 discharge | weighing · R6 valid_weighing | valid_claim ·
            R7 final_status | price_unit · R8 claim_reason FULL ·
            R9 quality | other · R10-11 roe / accept (2 hàng lẻ Access → gộp 1
            hàng lấp ô trống, giữ thứ tự — Bước 4) · R12 note FULL. */}
        <Box sx={{ ...FORM_GRID_2COL, columnGap: 1.5, rowGap: 1.25, alignItems: 'start' }}>
          {/* Row 1 — Claim ID (readonly, blank khi New) | ô trống (Access EmptyCell) */}
          <TextField label="Claim ID" size={SM} fullWidth value={mode === 'edit' && claimId ? String(claimId) : ''}
            InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: -1 }} sx={readonlyFieldSx} />
          <Box />

          {/* Row 2 — Claim No. (index, New editable / Edit khoá) | Customer Invoice Number */}
          <TextField label="Claim No." size={SM} fullWidth required value={draft.claimNoIndex}
            onChange={(e) => handleIndexChange(e.target.value)}
            onBlur={() => void handleIndexBlur()}
            error={indexEmpty}
            disabled={mode === 'edit'}
            inputProps={{ inputMode: 'numeric' }}
            helperText={mode === 'new' ? 'Suggested automatically; edit if needed.' : undefined} />
          <Combo label="Customer Invoice Number *" value={draft.customerInvoiceNumber}
            options={customerInvoices.data?.items ?? []} loading={customerInvoices.isFetching}
            disabled={mode === 'edit'} onInputChange={setInvQ}
            onPick={(v) => set('customerInvoiceNumber', v)} />

          {/* Row 3 — Claim Code (readonly, auto "CLAIM-<index>") | Claim Date */}
          {roField('claimScsCode', 'Claim Code')}
          {dateField('claimDate', 'Claim Date', true)}

          {/* Row 4 — Type of Claim | Conditions */}
          <Autocomplete
            size={SM} fullWidth options={typeMerged} value={typeSel} loading={types.isFetching}
            onChange={(_e, v) => handleTypePick(v?.label ?? '')}
            getOptionLabel={(o) => (!o ? '' : o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`)}
            isOptionEqualToValue={(a, b) => a.label === b.label}
            renderInput={(params) => <TextField {...params} label="Type of Claim" />}
          />
          <Combo label="Conditions" value={draft.conditions}
            options={conditions.data?.items ?? []} loading={conditions.isFetching}
            onPick={(v) => set('conditions', v)} />

          {/* Row 5 — Discharge Date | Weighing Date */}
          {dateField('dischargeDate', 'Discharge Date')}
          {dateField('weighingDate', 'Weighing Date')}

          {/* Row 6 — Valid Weighing Date | Valid Claim Date (CheckStatus, display-only) */}
          {roField('validWeighingDate', 'Valid Weighing Date')}
          {roField('validClaimDate', 'Valid Claim Date')}

          {/* Row 7 — Final Status | Price Unit */}
          <Combo label="Final Status" value={draft.finalStatus} options={FINAL_STATUS_OPTS} labelOnly
            onPick={(v) => set('finalStatus', v)} />
          <Combo label="Price Unit" value={draft.priceUnit} options={priceUnits.data?.items ?? []}
            loading={priceUnits.isFetching} onPick={(v) => set('priceUnit', v)} />

          {/* Row 8 — Reason Claim FULL WIDTH (Access Left 2676 → 13644, Height 564 ≈ 2 dòng) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            {textField('claimReason', 'Reason Claim', { multiline: true, minRows: 2 })}
          </Box>

          {/* Row 9 — Claim Quality/Other Value (enable theo claim_type cascade) */}
          {numField('claimQualityValue', 'Claim Quality Value', !qualityEnabled)}
          {numField('claimOtherValue', 'Claim Other Value', !otherEnabled)}

          {/* Row 10-11 (Access 2 hàng lẻ cột trái → gộp 1 hàng, giữ thứ tự) */}
          {numField('roe', 'ROE')}
          {roField('acceptOrReject', 'Accept/Reject')}

          {/* Row 12 — Remark FULL WIDTH (Access Height 852 ≈ 3 dòng) */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            {textField('note', 'Remark', { multiline: true, minRows: 3 })}
          </Box>
        </Box>
        </>
        )}
      </DialogContent>
    </Dialog>
  )
}
